import { ReactElement } from "react"

import { CreateCommentComponent } from "./createComment"
import { GenericCommentsCard } from "./genericComments/genericCommentsCard"
import { Datasets } from "../../constants/enums"
import { useGetLctTtrRbac } from "../../hooks/useGetLctTtrRbac"
import { useGetUserData } from "../../hooks/useGetUserData"
import useCreateComment from "../../services/middleware/hooks/deviceRegisters/useCreateComment"
import useDeleteComment from "../../services/middleware/hooks/deviceRegisters/useDeleteComment"
import usePinComment from "../../services/middleware/hooks/deviceRegisters/usePinComment"
import { IComment } from "../../services/middleware/mappers/singleDeviceMapper"

interface CommentsCardComponentProps {
    deviceSystemRef: string
    comments: IComment[] | undefined
    pinnedCommentId: string | undefined
    mutate: () => void
    fetchingComments: boolean
    isSubscribed?: boolean
    dataset?: Datasets
    isThreadUnread?: boolean
    lastReadBy?: string
    lastReadAt?: string
    isV2gDevice?: boolean
}

/**
 * Returns AppCard that contains comments and 'Add commment' field.
 */
export const CommentsCardComponent = ({
    deviceSystemRef,
    comments,
    pinnedCommentId,
    mutate,
    fetchingComments,
    dataset,
    isSubscribed,
    isThreadUnread,
    lastReadBy,
    lastReadAt,
    isV2gDevice,
}: CommentsCardComponentProps): ReactElement => {
    const { isEnaSysAdmin } = useGetUserData()

    const {
        error: deleteError,
        isSending: deleteLoading,
        postResponse: postDelete,
    } = useDeleteComment(isV2gDevice)

    const {
        error: createError,
        isSending: createLoading,
        postResponse: postCreate,
    } = useCreateComment(isV2gDevice)

    const {
        pinComment,
        unpinComment,
        error: pinError,
        isSending: pinLoading,
    } = usePinComment(isV2gDevice)

    const { canCreateComments, canPinComments } = useGetLctTtrRbac(
        deviceSystemRef,
        dataset,
        isV2gDevice
    )

    const deleteComment = (commentId: string): Promise<void> =>
        postDelete(deviceSystemRef, commentId, dataset)

    const pin = (commentId: string): Promise<void> =>
        pinComment(deviceSystemRef, commentId, dataset)

    const unpin = (): Promise<void> => unpinComment(deviceSystemRef, dataset)

    const renderCreateComponent = (isLoading: boolean): ReactElement => (
        <CreateCommentComponent
            deviceSystemRef={deviceSystemRef}
            mutate={mutate}
            isLoading={isLoading}
            createComment={postCreate}
            dataset={dataset}
            isThreadUnread={isThreadUnread}
            lastReadBy={lastReadBy}
            lastReadAt={lastReadAt}
            isV2gDevice={isV2gDevice}
        />
    )

    const canDeleteComment = (comment: IComment): boolean =>
        comment.fromRequestor === true || isEnaSysAdmin

    return (
        <GenericCommentsCard
            title="Comments"
            subheader="Use comments to communicate with other users about this device. Comments are visible only to the device manufacturer, ENA, evaluators and DNOs"
            fetchingComments={fetchingComments}
            deleteError={deleteError}
            deleteLoading={deleteLoading}
            postDelete={deleteComment}
            createError={createError}
            createLoading={createLoading}
            pinComment={pin}
            unpinComment={unpin}
            pinError={pinError}
            pinLoading={pinLoading}
            canCreateComments={canCreateComments}
            canPinComments={canPinComments}
            canDeleteComment={canDeleteComment}
            mutate={mutate}
            pinnedCommentId={pinnedCommentId}
            comments={comments}
            renderCreateComponent={renderCreateComponent}
            isSubscribed={isSubscribed}
            deviceSysRef={deviceSystemRef}
            deviceType={dataset}
        />
    )
}

CommentsCardComponent.defaultProps = {
    dataset: null,
    isSubscribed: false,
    isThreadUnread: false,
    lastReadBy: undefined,
    lastReadAt: undefined,
    isV2gDevice: false,
}
